import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '../organisms/Footer';
import NavBar from '../molecules/NavBar';
import { navigateToHash } from '../../utils/hashNavigation';

const sameDocumentHash = (anchor) => {
  if (!anchor || typeof anchor.getAttribute !== 'function') {
    return null;
  }

  const href = anchor.getAttribute('href');
  if (!href || !href.includes('#')) {
    return null;
  }

  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
      return null;
    }
    return url.hash;
  } catch {
    return href.startsWith('#') ? href : null;
  }
};

const PageLayout = ({ children }) => {
  const location = useLocation();
  const mainRef = useRef(null);
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    if (location.hash) {
      navigateToHash(location.hash);
      return;
    }

    mainRef.current?.focus({ preventScroll: false });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const applyWindowHash = () => {
      const hash = window.location.hash || locationRef.current.hash;
      if (hash) {
        navigateToHash(hash);
      }
    };

    const handleClick = (event) => {
      const anchor = event.target.closest?.('a[href]');
      const hash = sameDocumentHash(anchor);
      if (!hash) {
        return;
      }

      if (hash === window.location.hash || hash === locationRef.current.hash) {
        event.preventDefault();
        navigateToHash(hash);
      }
    };

    window.addEventListener('hashchange', applyWindowHash);
    window.addEventListener('popstate', applyWindowHash);
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('hashchange', applyWindowHash);
      window.removeEventListener('popstate', applyWindowHash);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <NavBar />
      <main
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        className="flex-grow w-full focus:outline-none"
      >
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default PageLayout;
